import type { CSSObject } from '@emotion/react'

import theme from '@theme'

/* -----------------------------------------------------------------------------
 * style props
 *
 * The kit components take the same style props the @walrus/common UI kit took
 * (`mt='16'`, `bg='white'`, `font='display.16.bold'`, `flex='1 0 auto'`) and
 * resolve them the way @walrus/themed and react-native-web 0.17 did, so the
 * screens render the same pixels without changing their JSX:
 * - each prop resolves against its theme scale, falling back to the raw value;
 * - props apply in the order they appear, later ones winning, and `style`
 *   applies last;
 * - the merged react-native style becomes CSS with react-native-web's rules
 *   (numbers are px, longhands beat shorthands, RN shadows become box-shadow).
 * -------------------------------------------------------------------------- */

export type RNStyle = Record<string, any>

type Transform = (value: any) => RNStyle
type PropDefinition = { transform: Transform; scale?: Record<string, any> }
export type StyleGroup = Record<string, PropDefinition>

// a scale value can itself be a style object (fonts, shadows): pass it through
const toKey =
  (cssKey: string): Transform =>
  value =>
    value !== null && typeof value === 'object' ? value : { [cssKey]: value }

const group = (
  entries: [string | string[], Transform, Record<string, any>?][]
): StyleGroup => {
  const definitions: StyleGroup = {}
  entries.forEach(([keys, transform, scale]) => {
    ;(Array.isArray(keys) ? keys : [keys]).forEach(key => {
      definitions[key] = { transform, scale }
    })
  })
  return definitions
}

const plain = (key: string): [string, Transform] => [key, toKey(key)]

/* ---- flex ------------------------------------------------------------------ */

// @walrus/themed's expandFlex, kept as is: `flex='1'` means `1 1 auto`
const expandFlex = (value: string | number) => {
  const parts = typeof value === 'string' ? value.split(' ') : [value]
  let flexGrow = 0
  let flexShrink = 1
  let flexBasis: string | number = 'auto'

  if (parts.length === 1) {
    if (isNaN(Number(parts[0]))) {
      if (parts[0] === 'none') {
        flexShrink = 0
      } else if (parts[0] === 'auto') {
        flexGrow = 1
      }
    }
    flexGrow = Number(parts[0])
  } else if (parts.length === 2) {
    flexGrow = Number(parts[0])
    flexBasis = 0
    if (isNaN(Number(parts[1]))) {
      flexBasis = parts[1]
    } else {
      flexShrink = Number(parts[1])
    }
  } else if (parts.length === 3) {
    flexGrow = Number(parts[0])
    flexShrink = Number(parts[1])
    flexBasis = isNaN(Number(parts[2])) ? parts[2] : Number(parts[2])
  }

  return { flexGrow, flexShrink, flexBasis }
}

/* ---- groups (same names as @walrus/themed) ---------------------------------- */

const sides =
  (prefix: string, names: string[]): Transform =>
  value =>
    Object.fromEntries(names.map(name => [`${prefix}${name}`, value]))

export const positioning = group([
  plain('position'),
  plain('top'),
  plain('right'),
  plain('bottom'),
  plain('left'),
  plain('zIndex')
])

export const layout = group([
  plain('display'),
  plain('overflowX'),
  plain('overflowY'),
  plain('verticalAlign')
])

export const spacing = group([
  [
    ['margin', 'm'],
    sides('margin', ['Top', 'Right', 'Bottom', 'Left']),
    theme.spaces
  ],
  [['marginX', 'mx'], sides('margin', ['Right', 'Left']), theme.spaces],
  [['marginY', 'my'], sides('margin', ['Top', 'Bottom']), theme.spaces],
  [['marginTop', 'mt'], toKey('marginTop'), theme.spaces],
  [['marginBottom', 'mb'], toKey('marginBottom'), theme.spaces],
  [['marginLeft', 'ml'], toKey('marginLeft'), theme.spaces],
  [['marginRight', 'mr'], toKey('marginRight'), theme.spaces],
  ['gap', toKey('gap'), theme.spaces],
  [
    ['padding', 'p'],
    sides('padding', ['Top', 'Right', 'Bottom', 'Left']),
    theme.spaces
  ],
  [['paddingX', 'px'], sides('padding', ['Right', 'Left']), theme.spaces],
  [['paddingY', 'py'], sides('padding', ['Top', 'Bottom']), theme.spaces],
  [['paddingTop', 'pt'], toKey('paddingTop'), theme.spaces],
  [['paddingBottom', 'pb'], toKey('paddingBottom'), theme.spaces],
  [['paddingLeft', 'pl'], toKey('paddingLeft'), theme.spaces],
  [['paddingRight', 'pr'], toKey('paddingRight'), theme.spaces]
])

// the margin half of `spacing` (Button's outer part takes only margins)
export const margins = Object.fromEntries(
  Object.entries(spacing).filter(
    ([key]) => key === 'gap' || key.startsWith('m')
  )
) as StyleGroup
export const paddings = Object.fromEntries(
  Object.entries(spacing).filter(([key]) => key.startsWith('p'))
) as StyleGroup

export const sizing = group([
  plain('width'),
  plain('maxWidth'),
  plain('minWidth'),
  plain('height'),
  plain('maxHeight'),
  plain('minHeight')
])

export const flexContainer = group([
  plain('flexBasis'),
  plain('flexDirection'),
  plain('flexWrap'),
  plain('alignContent'),
  plain('alignItems'),
  plain('justifyContent'),
  plain('justifyItems'),
  plain('placeContent'),
  plain('placeItems')
])

export const flexItem = group([
  ['flex', value => expandFlex(value)],
  plain('order'),
  plain('justifySelf'),
  plain('alignSelf'),
  plain('placeSelf')
])

export const coloring = group([
  [['backgroundColor', 'bg'], toKey('backgroundColor'), theme.colors],
  plain('opacity')
])

export const typography = group([
  ['font', toKey('font'), theme.fonts],
  plain('fontSize'),
  plain('fontFamily'),
  plain('fontWeight'),
  plain('fontStyle'),
  plain('textAlign'),
  plain('textDecorationLine'),
  plain('textTransform'),
  plain('wordBreak'),
  plain('lineHeight'),
  plain('letterSpacing'),
  ['textColor', toKey('color'), theme.colors]
])

export const border = group([
  plain('border'),
  plain('borderWidth'),
  plain('borderStyle'),
  ['borderColor', toKey('borderColor'), theme.colors],
  plain('borderTopWidth'),
  plain('borderRightWidth'),
  plain('borderBottomWidth'),
  plain('borderLeftWidth'),
  ['borderRadius', toKey('borderRadius'), theme.radii],
  ['borderTopLeftRadius', toKey('borderTopLeftRadius'), theme.radii],
  ['borderTopRightRadius', toKey('borderTopRightRadius'), theme.radii],
  ['borderBottomLeftRadius', toKey('borderBottomLeftRadius'), theme.radii],
  ['borderBottomRightRadius', toKey('borderBottomRightRadius'), theme.radii],
  ['boxShadow', toKey('boxShadow'), theme.shadows]
])

export const background = group([
  plain('background'),
  plain('backgroundImage'),
  plain('backgroundSize'),
  plain('backgroundRepeat'),
  plain('backgroundPosition')
])

export const composeGroups = (...groups: StyleGroup[]): StyleGroup =>
  Object.assign({}, ...groups)

/* -----------------------------------------------------------------------------
 * react-native style -> CSS (react-native-web 0.17's createReactDOMStyle)
 * -------------------------------------------------------------------------- */

const SYSTEM_FONT_STACK =
  '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif'

const UNITLESS = new Set([
  'animationIterationCount',
  'aspectRatio',
  'borderImageOutset',
  'borderImageSlice',
  'borderImageWidth',
  'boxFlex',
  'boxFlexGroup',
  'boxOrdinalGroup',
  'columnCount',
  'flex',
  'flexGrow',
  'flexOrder',
  'flexPositive',
  'flexShrink',
  'flexNegative',
  'fontWeight',
  'gridRow',
  'gridRowEnd',
  'gridRowGap',
  'gridRowStart',
  'gridColumn',
  'gridColumnEnd',
  'gridColumnGap',
  'gridColumnStart',
  'lineClamp',
  'opacity',
  'order',
  'orphans',
  'tabSize',
  'widows',
  'zIndex',
  'zoom',
  'fillOpacity',
  'floodOpacity',
  'stopOpacity',
  'strokeDasharray',
  'strokeDashoffset',
  'strokeMiterlimit',
  'strokeOpacity',
  'strokeWidth',
  'scale',
  'scaleX',
  'scaleY',
  'scaleZ',
  'shadowOpacity',
  'WebkitLineClamp'
])

const EXPANSIONS: Record<string, string[]> = {
  borderColor: [
    'borderTopColor',
    'borderRightColor',
    'borderBottomColor',
    'borderLeftColor'
  ],
  borderRadius: [
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderBottomRightRadius',
    'borderBottomLeftRadius'
  ],
  borderStyle: [
    'borderTopStyle',
    'borderRightStyle',
    'borderBottomStyle',
    'borderLeftStyle'
  ],
  borderWidth: [
    'borderTopWidth',
    'borderRightWidth',
    'borderBottomWidth',
    'borderLeftWidth'
  ],
  margin: ['marginTop', 'marginRight', 'marginBottom', 'marginLeft'],
  marginHorizontal: ['marginRight', 'marginLeft'],
  marginVertical: ['marginTop', 'marginBottom'],
  overflow: ['overflowX', 'overflowY'],
  overscrollBehavior: ['overscrollBehaviorX', 'overscrollBehaviorY'],
  padding: ['paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft'],
  paddingHorizontal: ['paddingRight', 'paddingLeft'],
  paddingVertical: ['paddingTop', 'paddingBottom']
}

const px = (value: any, prop?: string) =>
  typeof value === 'number' && !(prop && UNITLESS.has(prop))
    ? `${value}px`
    : value

// rgb()/rgba()/#hex with an extra opacity, as react-native-web's normalizeColor
const withOpacity = (color: string, opacity = 1) => {
  let r: number, g: number, b: number
  let a = 1
  const hex = color.match(/^#([0-9a-f]{3,8})$/i)
  const rgb = color.match(/^rgba?\(([^)]+)\)$/i)
  if (hex) {
    const h = hex[1].length <= 4 ? hex[1].replace(/./g, c => c + c) : hex[1]
    r = parseInt(h.slice(0, 2), 16)
    g = parseInt(h.slice(2, 4), 16)
    b = parseInt(h.slice(4, 6), 16)
    if (h.length === 8) a = parseInt(h.slice(6, 8), 16) / 255
  } else if (rgb) {
    const parts = rgb[1].split(',').map(part => parseFloat(part))
    ;[r, g, b] = parts
    if (parts.length > 3) a = parts[3]
  } else {
    return color
  }
  return `rgba(${r},${g},${b},${(a * opacity).toFixed(2)})`
}

const resolveShadow = (style: RNStyle) => {
  const { width = 0, height = 0 } = style.shadowOffset || {}
  return `${px(width)} ${px(height)} ${px(
    style.shadowRadius || 0
  )} ${withOpacity(style.shadowColor || 'black', style.shadowOpacity ?? 1)}`
}

const mapTransform = (transform: Record<string, any>) => {
  const type = Object.keys(transform)[0]
  const value = transform[type]
  if (type === 'matrix' || type === 'matrix3d')
    return `${type}(${value.join(',')})`
  return `${type}(${px(value, type)})`
}

export const toDOMStyle = (style: RNStyle): CSSObject => {
  const {
    shadowColor,
    shadowOffset,
    shadowOpacity,
    shadowRadius,
    elevation,
    ...rest
  } = style

  if (
    shadowColor != null ||
    shadowOffset != null ||
    shadowOpacity != null ||
    shadowRadius != null
  ) {
    const shadow = resolveShadow(style)
    rest.boxShadow = rest.boxShadow ? `${rest.boxShadow}, ${shadow}` : shadow
  }

  const css: Record<string, any> = {}
  Object.keys(rest)
    .sort()
    .forEach(prop => {
      const value = px(rest[prop], prop)
      if (value == null || (typeof value === 'number' && isNaN(value))) return

      switch (prop) {
        case 'overlayColor':
        case 'resizeMode':
        case 'tintColor':
          return
        case 'aspectRatio':
          css[prop] = value.toString()
          return
        case 'flex':
          if (value === -1) {
            css.flexGrow = 0
            css.flexShrink = 1
            css.flexBasis = 'auto'
          } else {
            css.flex = value
          }
          return
        case 'font':
          css.font = value.replace('System', SYSTEM_FONT_STACK)
          return
        case 'fontFamily':
          css.fontFamily = value.includes('System')
            ? value
                .split(/,\s*/)
                .map((family: string) =>
                  family === 'System' ? SYSTEM_FONT_STACK : family
                )
                .join(',')
            : value
          return
        case 'textAlignVertical':
          css.verticalAlign = value === 'center' ? 'middle' : value
          return
        case 'transform':
          css.transform = Array.isArray(value)
            ? value.map(mapTransform).join(' ')
            : value
          return
        case 'writingDirection':
          css.direction = value
          return
        default: {
          const longForms = EXPANSIONS[prop]
          if (longForms) {
            longForms.forEach(longForm => {
              if (typeof rest[longForm] === 'undefined') css[longForm] = value
            })
          } else {
            css[prop] = Array.isArray(value) ? value.join(',') : value
          }
        }
      }
    })

  return css
}

/* -----------------------------------------------------------------------------
 * resolving props
 * -------------------------------------------------------------------------- */

const BREAKPOINTS = Object.entries(theme.breakpoints).sort(
  (a, b) => a[1] - b[1]
)

const isResponsive = (value: any) =>
  value !== null &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.keys(value).some(key => key in theme.breakpoints)

// react-native style arrays nest and may hold false/null entries
export const flattenStyle = (style: any): RNStyle => {
  if (!style) return {}
  if (Array.isArray(style)) return Object.assign({}, ...style.map(flattenStyle))
  return style
}

/*
 * react-native-web kept two layers besides the component's reset: values a
 * StyleSheet registered (the theme scales) became atomic classes, everything
 * else (raw values, `style` objects) became inline styles, which win. The same
 * key lives in whichever layer wrote it last. Shorthand and longhand only
 * compete across layers: an inline `padding` beats a token `pt='16'`.
 */
type Layers = { classes: RNStyle; inline: RNStyle }

const write = (layers: Layers, into: keyof Layers, style: RNStyle) => {
  Object.keys(style).forEach(key => {
    delete layers.classes[key]
    delete layers.inline[key]
    layers[into][key] = style[key]
  })
}

const resolveAt = (
  props: Record<string, any>,
  definitions: StyleGroup,
  breakpoint: string | null,
  layers: Layers
) => {
  Object.keys(props).forEach(prop => {
    const definition = definitions[prop]
    if (!definition) return
    let value = props[prop]
    if (breakpoint && isResponsive(value)) {
      // mobile first: the closest breakpoint at or below this one wins
      let picked
      for (const [name] of BREAKPOINTS) {
        if (typeof value[name] !== 'undefined') picked = value[name]
        if (name === breakpoint) break
      }
      value = picked
    }
    const fromScale =
      !!definition.scale && typeof definition.scale[value] !== 'undefined'
    write(
      layers,
      fromScale ? 'classes' : 'inline',
      definition.transform(fromScale ? definition.scale[value] : value)
    )
  })
}

export type ResolvedStyle = { css: CSSObject; rest: Record<string, any> }

export type StyleLayers = {
  // the component's reset (react-native-web's css.create classes)
  base?: RNStyle
  // registered styles applied before the props (e.g. Pressable's cursor)
  classes?: RNStyle
  // inline styles applied after `style` (e.g. Text's line clamp)
  inline?: RNStyle
}

// later layers win: keys they add land after the earlier ones in the CSS
const cascade = (...styles: Record<string, any>[]) =>
  Object.assign({}, ...styles)

/**
 * Splits `props` into CSS (from the style props in `definitions`, the
 * component's layers and `style`) and the remaining props, which the
 * component forwards.
 */
export const resolveStyleProps = (
  props: Record<string, any>,
  definitions: StyleGroup,
  { base = {}, classes = {}, inline = {} }: StyleLayers = {}
): ResolvedStyle => {
  const rest: Record<string, any> = {}
  let responsive = false
  Object.keys(props).forEach(prop => {
    if (prop === 'style') return
    if (definitions[prop]) {
      if (isResponsive(props[prop])) responsive = true
    } else {
      rest[prop] = props[prop]
    }
  })

  const cssAt = (breakpoint: string | null) => {
    const layers: Layers = { classes: { ...classes }, inline: {} }
    resolveAt(props, definitions, breakpoint, layers)
    write(layers, 'inline', flattenStyle(props.style))
    write(layers, 'inline', inline)
    return cascade(
      toDOMStyle(base),
      toDOMStyle(layers.classes),
      toDOMStyle(layers.inline)
    )
  }

  if (!responsive) return { css: cssAt(null), rest }

  // one pass per breakpoint; each media query keeps only what changed
  const css: CSSObject = {}
  let previous: Record<string, any> = {}
  BREAKPOINTS.forEach(([name, width], index) => {
    const current = cssAt(name) as Record<string, any>
    if (index === 0) {
      Object.assign(css, current)
    } else {
      const changed: Record<string, any> = {}
      new Set([...Object.keys(previous), ...Object.keys(current)]).forEach(
        key => {
          if (previous[key] !== current[key])
            changed[key] = current[key] ?? 'revert'
        }
      )
      if (Object.keys(changed).length)
        css[`@media (min-width: ${width}px)`] = changed
    }
    previous = current
  })
  return { css, rest }
}
